import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast, Toaster } from "sonner";
import {
  BarChart3, TrendingUp, DollarSign, Users, MessageSquare, Calendar, Zap,
  Plus, X, Trash2, RefreshCw, LineChart as LineChartIcon, Sparkles,
} from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, Area, AreaChart,
} from "recharts";

const PALETTE = ["#FFB020", "#0A0A0A", "#3B82F6", "#34D399", "#F87171", "#A78BFA", "#F59E0B", "#EC4899"];
const fmtMoney = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

export default function Reports() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expenses, setExpenses] = useState([]);
  const [showExpModal, setShowExpModal] = useState(false);
  const [expForm, setExpForm] = useState({ description: "", amount: "", category: "Software", date: "", notes: "" });
  const [forecastHorizon, setForecastHorizon] = useState("6m");

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: d }, { data: e }] = await Promise.all([
        api.get("/reports/overview"),
        api.get("/reports/expenses"),
      ]);
      setData(d);
      setExpenses(e.expenses || []);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const addExpense = async () => {
    try {
      await api.post("/reports/expenses", {
        description: expForm.description,
        amount: parseFloat(expForm.amount),
        category: expForm.category,
        date: expForm.date || undefined,
        notes: expForm.notes,
      });
      toast.success("Expense added.");
      setShowExpModal(false);
      setExpForm({ description: "", amount: "", category: "Software", date: "", notes: "" });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const removeExpense = async (id) => {
    if (!window.confirm("Delete this expense?")) return;
    try { await api.delete(`/reports/expenses/${id}`); load(); toast.success("Deleted."); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const combinedSeries = useMemo(() => {
    if (!data) return [];
    const src = data.series;
    return src.leads.map((row, i) => ({
      date: row.date.slice(5),
      leads:         src.leads[i].count,
      clients:       src.clients[i].count,
      conversations: src.conversations[i].count,
      roi_signals:   src.roi_signals[i].count,
      bookings:      src.bookings[i].count,
      outreach:      src.outreach[i].count,
    }));
  }, [data]);

  return (
    <div className="p-8 max-w-7xl" data-testid="reports-page">
      <Toaster position="top-right" richColors />

      <div className="flex items-start justify-between gap-4 mb-8 flex-wrap">
        <div>
          <div className="overline mb-2 flex items-center gap-2">
            <BarChart3 className="w-3.5 h-3.5" /> Analytics
          </div>
          <h1 className="text-3xl font-bold text-neutral-900">Reports</h1>
          <p className="text-neutral-600 mt-2 text-sm">Everything happening in your funnel — from anonymous visitors to executed contracts.</p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/reports/ai-visibility"
                data-testid="reports-ai-visibility-tab"
                className="text-sm bg-[#E24E1B] text-white px-3 py-2 hover:bg-[#0F2340] transition inline-flex items-center gap-2 font-semibold">
            <Sparkles className="w-4 h-4" /> AI Visibility Audits
          </Link>
          <button data-testid="reports-refresh-btn" onClick={load} className="lf-btn-outline text-sm flex items-center gap-2">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
      </div>

      {loading || !data ? (
        <div className="p-12 text-center text-neutral-400">Loading…</div>
      ) : (
        <>
          {/* Revenue accounting */}
          <div className="mb-8">
            <div className="overline mb-4">Revenue & accounting</div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <RevStat label="MRR" value={fmtMoney(data.revenue.mrr)} icon={DollarSign} accent="green" testId="rev-mrr" />
              <RevStat label="ARR" value={fmtMoney(data.revenue.arr)} icon={TrendingUp} testId="rev-arr" />
              <RevStat label="Setup fees YTD" value={fmtMoney(data.revenue.setup_fee_ytd)} testId="rev-setup" />
              <RevStat label="Expenses YTD" value={fmtMoney(data.revenue.expense_ytd)} icon={X} accent="red" testId="rev-expense" />
              <RevStat label="Net YTD (est.)" value={fmtMoney(data.revenue.net_ytd)} icon={DollarSign} accent={data.revenue.net_ytd >= 0 ? "green" : "red"} testId="rev-net" />
            </div>
          </div>

          {/* Revenue projection */}
          {data.forecast && (
            <ForecastPanel
              forecast={data.forecast}
              horizon={forecastHorizon}
              setHorizon={setForecastHorizon}
            />
          )}

          {/* KPIs */}
          <div className="mb-8">
            <div className="overline mb-4">Traffic & conversion</div>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
              <Kpi label="ROI sessions" v={data.kpis.roi_sessions} sub={`${data.kpis.roi_hot} hot`} testId="kpi-roi" />
              <Kpi label="Conversations" v={data.kpis.conversations} sub={`${data.kpis.high_intent_chats} hi-intent`} testId="kpi-conv" />
              <Kpi label="Inbound submits" v={data.kpis.public_leads} testId="kpi-inbound" />
              <Kpi label="Leads" v={data.kpis.leads_total} sub={`${data.kpis.leads_won} won · ${data.kpis.leads_lost} lost`} testId="kpi-leads" />
              <Kpi label="Bookings" v={data.kpis.bookings} testId="kpi-bookings" />
              <Kpi label="Outreach sent" v={data.kpis.outreach_sent} sub={data.kpis.outreach_failed > 0 ? `${data.kpis.outreach_failed} failed` : ""} testId="kpi-outreach" />
              <Kpi label="Clients" v={data.kpis.clients_total} sub={`${data.kpis.clients_active} active · ${data.kpis.clients_trial} trial`} testId="kpi-clients" />
              <Kpi label="Docs signed" v={data.kpis.onboarding_docs_signed} sub={`of ${data.kpis.onboarding_docs_generated}`} testId="kpi-signed" />
              <Kpi label="Docs executed" v={data.kpis.onboarding_docs_executed} testId="kpi-executed" />
              <Kpi label="Sequences active" v={data.kpis.sequences_active} sub={`${data.kpis.sequences_completed} completed`} testId="kpi-sequences" />
            </div>
          </div>

          {/* 30-day line chart */}
          <div className="mb-8 bg-white border border-neutral-200 p-6">
            <div className="overline mb-4">Last 30 days — activity trend</div>
            <div style={{ width: "100%", height: 320 }}>
              <ResponsiveContainer>
                <LineChart data={combinedSeries}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} interval={2} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line type="monotone" dataKey="roi_signals" stroke="#FFB020" strokeWidth={2} dot={false} name="ROI sessions" />
                  <Line type="monotone" dataKey="conversations" stroke="#3B82F6" strokeWidth={2} dot={false} name="Conversations" />
                  <Line type="monotone" dataKey="leads" stroke="#0A0A0A" strokeWidth={2} dot={false} name="New leads" />
                  <Line type="monotone" dataKey="clients" stroke="#34D399" strokeWidth={2} dot={false} name="New clients" />
                  <Line type="monotone" dataKey="bookings" stroke="#A78BFA" strokeWidth={2} dot={false} name="Bookings" />
                  <Line type="monotone" dataKey="outreach" stroke="#F87171" strokeWidth={2} dot={false} name="Outreach sent" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Two-column charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8">
            <div className="bg-white border border-neutral-200 p-6">
              <div className="overline mb-4">Conversion funnel</div>
              <div style={{ width: "100%", height: 280 }}>
                <ResponsiveContainer>
                  <BarChart data={data.funnel} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                    <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                    <YAxis type="category" dataKey="stage" tick={{ fontSize: 11 }} width={160} />
                    <Tooltip />
                    <Bar dataKey="count" fill="#FFB020" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="bg-white border border-neutral-200 p-6">
              <div className="overline mb-4">Leads by status</div>
              <div style={{ width: "100%", height: 280 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={data.lead_by_status.filter((r) => r.count > 0)} dataKey="count" nameKey="status" outerRadius={100} label>
                      {data.lead_by_status.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                    </Pie>
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="bg-white border border-neutral-200 p-6">
              <div className="overline mb-4">Clients by status</div>
              <div style={{ width: "100%", height: 280 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={data.client_by_status.filter((r) => r.count > 0)} dataKey="count" nameKey="status" outerRadius={100} label>
                      {data.client_by_status.map((_, i) => <Cell key={i} fill={PALETTE[(i + 2) % PALETTE.length]} />)}
                    </Pie>
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="bg-white border border-neutral-200 p-6">
              <div className="overline mb-4">Clients by industry (top 10)</div>
              <div style={{ width: "100%", height: 280 }}>
                <ResponsiveContainer>
                  <BarChart data={data.clients_by_industry}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                    <XAxis dataKey="industry" tick={{ fontSize: 10 }} angle={-30} textAnchor="end" height={70} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <Tooltip />
                    <Bar dataKey="count" fill="#0A0A0A" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Expenses */}
          <div className="bg-white border border-neutral-200 p-6 mb-8">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <div>
                <div className="overline">Expenses</div>
                <div className="text-sm text-neutral-500 mt-1">Track your business expenses. Auto-flows into net YTD above.</div>
              </div>
              <button data-testid="reports-add-expense-btn" onClick={() => setShowExpModal(true)} className="lf-btn-primary text-sm flex items-center gap-2">
                <Plus className="w-4 h-4" /> Add expense
              </button>
            </div>
            {expenses.length === 0 ? (
              <div className="text-center text-sm text-neutral-400 py-8">No expenses logged yet.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="text-xs text-neutral-500 uppercase tracking-widest font-mono">
                  <tr><th className="text-left py-2">Date</th><th className="text-left py-2">Description</th><th className="text-left py-2">Category</th><th className="text-right py-2">Amount</th><th></th></tr>
                </thead>
                <tbody>
                  {expenses.map((e) => (
                    <tr key={e.id} className="border-t border-neutral-100" data-testid={`expense-row-${e.id.slice(0, 8)}`}>
                      <td className="py-2 font-mono text-xs">{e.date}</td>
                      <td className="py-2">{e.description}</td>
                      <td className="py-2"><span className="text-xs px-2 py-0.5 bg-neutral-100 border border-neutral-200">{e.category}</span></td>
                      <td className="py-2 text-right font-mono font-semibold">{fmtMoney(e.amount)}</td>
                      <td className="py-2 text-right"><button onClick={() => removeExpense(e.id)} className="text-neutral-400 hover:text-red-700"><Trash2 className="w-3.5 h-3.5" /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {/* Add expense modal */}
      {showExpModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setShowExpModal(false)}>
          <div className="bg-white border border-neutral-200 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-neutral-100 flex items-center justify-between">
              <div className="text-lg font-bold">Add expense</div>
              <button onClick={() => setShowExpModal(false)}><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-3">
              <input data-testid="expense-desc" className="lf-input" placeholder="What was it for?" value={expForm.description} onChange={(e) => setExpForm({ ...expForm, description: e.target.value })} />
              <div className="grid grid-cols-2 gap-3">
                <input data-testid="expense-amount" className="lf-input" type="number" placeholder="Amount" value={expForm.amount} onChange={(e) => setExpForm({ ...expForm, amount: e.target.value })} />
                <select data-testid="expense-category" className="lf-input" value={expForm.category} onChange={(e) => setExpForm({ ...expForm, category: e.target.value })}>
                  <option>Software</option><option>Hosting</option><option>API costs</option><option>Advertising</option>
                  <option>Domain</option><option>Contractor</option><option>Legal</option><option>Other</option>
                </select>
              </div>
              <input data-testid="expense-date" className="lf-input" type="date" value={expForm.date} onChange={(e) => setExpForm({ ...expForm, date: e.target.value })} />
              <textarea data-testid="expense-notes" className="lf-input" placeholder="Notes (optional)" rows={2} value={expForm.notes} onChange={(e) => setExpForm({ ...expForm, notes: e.target.value })} />
            </div>
            <div className="px-6 py-4 border-t border-neutral-100 flex justify-end gap-2">
              <button onClick={() => setShowExpModal(false)} className="lf-btn-outline text-sm">Cancel</button>
              <button data-testid="expense-save" onClick={addExpense} disabled={!expForm.description || !expForm.amount} className="lf-btn-primary text-sm">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function RevStat({ label, value, icon: Icon, accent, testId }) {
  const colors = { green: "border-green-200 bg-green-50 text-green-800", red: "border-red-200 bg-red-50 text-red-800", default: "border-neutral-200 bg-white text-neutral-900" };
  const cls = colors[accent] || colors.default;
  return (
    <div className={`border p-4 ${cls}`} data-testid={testId}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-mono opacity-70">{Icon && <Icon className="w-3 h-3" />}{label}</div>
      <div className="text-2xl font-bold mt-1">{value}</div>
    </div>
  );
}

function Kpi({ label, v, sub, testId }) {
  return (
    <div className="border border-neutral-200 bg-white p-3" data-testid={testId}>
      <div className="text-xs uppercase tracking-wider font-mono text-neutral-500">{label}</div>
      <div className="text-xl font-bold mt-1">{v.toLocaleString()}</div>
      {sub && <div className="text-xs text-neutral-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function ForecastPanel({ forecast, horizon, setHorizon }) {
  const fmtMoney = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  const horizonData = forecast.horizons[horizon];
  const a = forecast.assumptions;
  if (!horizonData) return null;

  // Include month 0 (current MRR) so the curve starts from today
  const chartData = [
    { month: "Now", projected_mrr: a.current_mrr },
    ...horizonData.curve.map((r) => ({ month: `M${r.month}`, projected_mrr: r.projected_mrr })),
  ];

  const horizons = [
    { key: "3m", label: "3 mo" },
    { key: "6m", label: "6 mo" },
    { key: "12m", label: "12 mo" },
  ];

  return (
    <div className="mb-8" data-testid="forecast-panel">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="overline flex items-center gap-2">
          <LineChartIcon className="w-3.5 h-3.5" /> Revenue projection · conservative
        </div>
        <div className="inline-flex border border-neutral-200 bg-white" role="tablist" data-testid="forecast-horizon-tabs">
          {horizons.map((h) => (
            <button
              key={h.key}
              onClick={() => setHorizon(h.key)}
              data-testid={`forecast-horizon-${h.key}`}
              className={`px-3 py-1.5 text-xs font-mono uppercase tracking-widest transition-colors ${
                horizon === h.key
                  ? "bg-neutral-900 text-white"
                  : "text-neutral-600 hover:text-neutral-900"
              }`}
            >
              {h.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        <RevStat
          label={`Projected MRR (end of ${horizon})`}
          value={fmtMoney(horizonData.projected_mrr_end)}
          icon={TrendingUp}
          accent="green"
          testId="forecast-mrr-end"
        />
        <RevStat
          label={`Projected ARR (end of ${horizon})`}
          value={fmtMoney(horizonData.projected_arr_end)}
          icon={DollarSign}
          testId="forecast-arr-end"
        />
        <RevStat
          label="MRR delta vs today"
          value={`+${fmtMoney(horizonData.delta_mrr)}`}
          icon={TrendingUp}
          accent={horizonData.delta_mrr > 0 ? "green" : "default"}
          testId="forecast-delta"
        />
      </div>

      <div className="bg-white border border-neutral-200 p-6">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-4">
          <div>
            <div className="text-sm font-semibold text-neutral-900">MRR forecast curve</div>
            <div className="text-xs text-neutral-500 mt-1">
              Based on the last 30 days of new active/trial clients ({a.clients_added_30d} adds), a {(a.safety_margin * 100).toFixed(0)}% safety multiplier, {(a.trial_to_paid * 100).toFixed(0)}% trial-to-paid, and {(a.monthly_churn * 100).toFixed(0)}% monthly churn.
            </div>
          </div>
          <div className="text-xs font-mono text-neutral-500">
            {a.monthly_new_clients} new/mo · avg {fmtMoney(a.avg_client_mrr)} MRR
          </div>
        </div>
        <div style={{ width: "100%", height: 280 }} data-testid="forecast-chart">
          <ResponsiveContainer>
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="mrrGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#FFB020" stopOpacity={0.55} />
                  <stop offset="100%" stopColor="#FFB020" stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => fmtMoney(v)} />
              <Area
                type="monotone"
                dataKey="projected_mrr"
                stroke="#FFB020"
                strokeWidth={2.5}
                fill="url(#mrrGradient)"
                name="Projected MRR"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
